import { useCallback, useContext, useEffect, useRef } from 'react';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { VoiceModeContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { ChatQueryStatus } from '@trinity/chat';
import { QuerySocketEvent } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import { getQuerySiblings } from '@trinity/chat-components/utils';
import { useChatQuerySocket } from '@trinity/chat-components/websocket';
import type { WS } from '@trinity/chat';
import { ChatSessionQueriesContext } from './context';
import { useScrollToBottomOfMessages } from './hooks/useScrollMessageList';

// Toggle this to enable/disable debug logging for QueryEventsContainer
const DEBUG = true;

const debug = (...args: any[]) => {
  if (DEBUG) {
    console.log('[QueryEventsContainer]', ...args);
  }
};

type Props = {
  children: React.ReactNode;
  pollingEnabed?: boolean;
  queryIdentifier: string;
};

type QueryAvailable = {
  chatInstance: Chat.Instance;
  query: Chat.Query;
  regenerated: boolean;
};

type QueryErrored = {
  langfuseTraceUrl: string | null;
  queryIdentifier: string;
  traceId: string;
};

const useGetQuerySiblings = () => {
  const queries = useContext(ChatSessionQueriesContext);

  return useCallback((queryIdentifier: string) => {
    return getQuerySiblings({
      queryIdentifier,
      queries,
    });
  }, [queries]);
};

export const QueryEventsContainer = ({ queryIdentifier, children, pollingEnabed = true }: Props) => {

  const scrollToBottom = useScrollToBottomOfMessages();
  const dispatch = useContext(ChatStateDispatchContext);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const getQuerySiblings = useGetQuerySiblings();
  const voicemode = useContext(VoiceModeContext);

  const handleQueryErrored = useCallback((data: QueryErrored) => {
    dispatch({
      type: 'query-errored',
      payload: {
        langfuseTraceUrl: data.langfuseTraceUrl,
        queryIdentifier: data.queryIdentifier,
        traceId: data.traceId,
      },
    });
    scrollToBottom();
  }, [dispatch, scrollToBottom]);

  const handleQueryAvailable = useCallback((data: QueryAvailable) => {
    if (data.regenerated) {
      dispatch({
        type: 'query-variant-response-available',
        payload: {
          chatInstance: data.chatInstance,
          query: data.query,
        },
      });
    } else {
      dispatch({
        type: 'query-response-available',
        payload: {
          chatInstance: data.chatInstance,
          query: data.query,
        },
      }); if (voicemode.enabled && data.query.response) {
        debug('Enqueuing voice mode for query:', data.query.id);
        voicemode.enqueue({
          queryId: data.query.id,
          type: data.query.response.type,
        });
      }
    }
    scrollToBottom();
  }, [dispatch, scrollToBottom, voicemode]);

  const handleQueryStreaming = useCallback((data: WS.QueryResponseStreaming.Data) => {
    dispatch({
      type: 'query-response-streaming',
      payload: {
        queryIdentifier: data.queryIdentifier,
        textChunk: data.textChunk,
      },
    });

    scrollToBottom();
  }, [dispatch, scrollToBottom]);

  const apiClient = useChatApiClient();

  const getPendingQuery = useCallback(() => {
    return apiClient.chat.getPendingQuery({
      queryIdentifier,
    }).then(res => {
      if (!res.query) return;

      let keepPolling = false;
      if (res.query.status === ChatQueryStatus.Error) {
        debug('Polling: Query errored -', queryIdentifier);
        handleQueryErrored({
          queryIdentifier: res.query.identifier,
          langfuseTraceUrl: res.query.langfuseTraceUrl,
          traceId: null,
        });
      } else if (res.query.status === ChatQueryStatus.Complete || res.query.status === ChatQueryStatus.FollowupsAvailable) {
        debug('Polling: Query available -', queryIdentifier, 'Status:', res.query.status);
        // if (!res.chatInstance.name) {
        //   keepPolling = true;
        //   debug('Polling: Chat instance name missing, continuing poll -', queryIdentifier);
        // } else {

        const siblings = getQuerySiblings(res.query.mapping.parentIdentifier);
        const regenerated = siblings.length > 1;

        debug('Query regenerated:', regenerated, 'Siblings:', siblings.length);

        handleQueryAvailable({
          chatInstance: res.chatInstance,
          query: res.query,
          regenerated,
        });
        // }
      } else {
        debug('Polling: Query pending -', queryIdentifier, 'Status:', res.query.status);
        keepPolling = true;
      }

      if (keepPolling) {
        const pollingInterval = 2000;
        pollingTimeoutRef.current = setTimeout(() => {
          debug('Polling for query -', queryIdentifier);
          return getPendingQuery();
        }, pollingInterval);
      }
    });
  }, [apiClient, getQuerySiblings, handleQueryAvailable, handleQueryErrored, queryIdentifier]);

  useEffect(() => {
    if (!pollingEnabed) return;

    if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);

    pollingTimeoutRef.current = setTimeout(() => {
      console.log('Start polling for query', queryIdentifier);
      return getPendingQuery();
    }, 10000);

    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, [getPendingQuery, queryIdentifier, pollingEnabed]);

  const handleQueryAvailableEvent = useCallback((data: WS.QueryAvailable.Data) => {
    debug('WebSocket: QueryAvailable received -', data.queryIdentifier, 'Regenerated:', data.regenerated);
    if (data.queryIdentifier !== queryIdentifier) {
      debug('WebSocket: Ignoring QueryAvailable - expected:', queryIdentifier, 'received:', data.queryIdentifier);
      return;
    }

    if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);

    return apiClient.queries.getQuery({
      chatInstanceId: data.chatId,
      queryIdentifier: data.queryIdentifier,
    }).then(res => {
      handleQueryAvailable({
        chatInstance: res.chatInstance,
        query: res.query,
        regenerated: data.regenerated,
      });
    });
  }, [apiClient, handleQueryAvailable, queryIdentifier]);

  const handleQueryResponseStreamingEvent = useCallback((data: WS.QueryResponseStreaming.Data) => {
    if (data.queryIdentifier !== queryIdentifier) return;
    handleQueryStreaming(data);
  }, [handleQueryStreaming, queryIdentifier]);

  const handleTakingTooLongEvent = useCallback((data: WS.QueryTakingTooLong.Data) => {
    debug('WebSocket: QueryTakingTooLong received -', data.identifier);
    if (data.identifier !== queryIdentifier) {
      debug('WebSocket: Ignoring QueryTakingTooLong - expected:', queryIdentifier, 'received:', data.identifier);
      return;
    }
    dispatch({
      type: 'query-taking-long',
      payload: {
        queryIdentifier: data.identifier,
      },
    });
  }, [dispatch, queryIdentifier]);

  const handleQueryErroredEvent = useCallback((data: WS.QueryErrored.Data) => {
    debug('WebSocket: QueryErrored received -', data.queryIdentifier, 'Trace:', data.traceId);
    if (data.queryIdentifier !== queryIdentifier) {
      debug('WebSocket: Ignoring QueryErrored - expected:', queryIdentifier, 'received:', data.queryIdentifier);
      return;
    }

    handleQueryErrored(data);
  }, [handleQueryErrored, queryIdentifier]);

  // Use refs to store latest handlers to avoid re-registering listeners
  const handlersRef = useRef({
    handleQueryAvailableEvent,
    handleQueryResponseStreamingEvent,
    handleQueryErroredEvent,
    handleTakingTooLongEvent,
  });

  useEffect(() => {
    handlersRef.current = {
      handleQueryAvailableEvent,
      handleQueryResponseStreamingEvent,
      handleQueryErroredEvent,
      handleTakingTooLongEvent,
    };
  });

  const socket = useChatQuerySocket(queryIdentifier);

  useEffect(() => {
    if (!socket) {
      debug('Socket: No socket available for query -', queryIdentifier);
      return;
    };

    debug('Socket: Setting up listeners for query -', queryIdentifier);

    // Create stable callback references that use the latest handlers from ref
    const stableQueryAvailable = (data: WS.QueryAvailable.Data) => handlersRef.current.handleQueryAvailableEvent(data);
    const stableQueryStreaming = (data: WS.QueryResponseStreaming.Data) => handlersRef.current.handleQueryResponseStreamingEvent(data);
    const stableQueryErrored = (data: WS.QueryErrored.Data) => handlersRef.current.handleQueryErroredEvent(data);
    const stableTakingTooLong = (data: WS.QueryTakingTooLong.Data) => handlersRef.current.handleTakingTooLongEvent(data);

    socket.on(QuerySocketEvent.QueryAvailable, stableQueryAvailable);
    socket.on(QuerySocketEvent.QueryResponseStreaming, stableQueryStreaming);
    socket.on(QuerySocketEvent.QueryErrored, stableQueryErrored);
    socket.on(QuerySocketEvent.QueryTakingTooLong, stableTakingTooLong);

    return () => {
      debug('Socket: Cleaning up listeners for query -', queryIdentifier);
      socket.off(QuerySocketEvent.QueryAvailable, stableQueryAvailable);
      socket.off(QuerySocketEvent.QueryResponseStreaming, stableQueryStreaming);
      socket.off(QuerySocketEvent.QueryErrored, stableQueryErrored);
      socket.off(QuerySocketEvent.QueryTakingTooLong, stableTakingTooLong);
    };
  }, [socket, queryIdentifier]);

  return (
    <>
      {children}
    </>
  );
};